/**
 * React Query hooks around the `notifications` inbox table.
 *
 * `notifications` is the fired/in-app version of scheduled_notifications —
 * the edge function inserts a row here every time it delivers a push, plus
 * any ad-hoc messages admins/code paths want to surface even without push
 * permission.
 *
 * Schema (migration 20260217030122):
 *   id, user_id, title, message, type, read (bool), link, created_at
 *
 * Design choices:
 *   • 30-second refetch interval — push arrives via SW but if the tab is
 *     in the foreground, polling keeps the bell fresh without needing a
 *     realtime subscription wired up (can upgrade to realtime later).
 *   • Mutation invalidates the list query so the unread count + visual
 *     state sync after mark-as-read.
 *   • Only pull the 30 most recent. Anything older the user can view on a
 *     dedicated inbox page (future v2) — for the bell we only care about
 *     what's actionable now.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/features/auth/AuthProvider";

export type InboxNotification = {
  id: string;
  title: string;
  message: string;
  type: string;
  read: boolean;
  link: string | null;
  created_at: string;
};

const INBOX_LIMIT = 30;

export function useNotifications() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["notifications", user?.id],
    enabled: Boolean(user?.id),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<InboxNotification[]> => {
      const { data, error } = await supabase
        .from("notifications")
        .select("id, title, message, type, read, link, created_at")
        .order("created_at", { ascending: false })
        .limit(INBOX_LIMIT);

      if (error) throw error;
      return (data ?? []) as InboxNotification[];
    },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications", user?.id] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user) return;
      // RLS limits the scope to the caller's own rows, but we still filter
      // client-side — makes the intent explicit and skips RLS row fan-out
      // on rows that are already read.
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("user_id", user.id)
        .eq("read", false);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications", user?.id] });
    },
  });
}
